/**
 * UIResourceRenderer - Renders MCP-UI resources in chat messages
 * 
 * Supports rendering of:
 * - HTML resources (maps, charts, etc.) via iframe
 * - Container route maps using React RouteMap component (preferred)
 * - Other MCP-UI resource types
 * 
 * Uses @mcp-ui/client for rendering UI resources
 */

import { UIResourceRenderer as MCPUIResourceRenderer } from "@mcp-ui/client";
import { useMemo, useEffect, useRef } from "react";
import { Link } from "react-router";
import { RouteMap } from "@/components/application/map/route-map";
import { Button } from "@/components/base/buttons/button";
import type { Container } from "@/types/container";
import type { Waypoint } from "@/components/application/map/route-map";

export interface UIResource {
    uri: string;
    name?: string;
    mimeType: string;
    text?: string;
    blob?: string;
    _meta?: {
        "mcpui.dev/ui-preferred-frame-size"?: {
            width: number;
            height: number;
        };
        "mcpui.dev/ui-initial-render-data"?: Record<string, any>;
        [key: string]: any;
    };
    [key: string]: any;
}

interface UIResourceRendererProps {
    resource: UIResource;
    className?: string;
}

// Component to handle iframe map initialization
const MapIframe = ({ htmlContent, title }: { htmlContent: string; title?: string }) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        // Wait for iframe to load, then try to resize Leaflet map if present
        const handleLoad = () => {
            try {
                const iframeWindow = iframe.contentWindow;
                if (iframeWindow) {
                    // Try to trigger Leaflet map resize if it exists
                    // Leaflet maps need to be invalidated when container size changes
                    setTimeout(() => {
                        try {
                            const L = (iframeWindow as Window & { L?: { map?: { instances?: unknown[] } } }).L;
                            const instances = L?.map?.instances;
                            if (Array.isArray(instances)) {
                                instances.forEach((map: unknown) => {
                                    if (map && typeof (map as { invalidateSize?: () => void }).invalidateSize === 'function') {
                                        (map as { invalidateSize: () => void }).invalidateSize();
                                    }
                                });
                            }
                        } catch (e) {
                            // Silently fail if we can't access iframe content
                            console.debug("Could not resize map in iframe:", e);
                        }
                    }, 500);
                }
            } catch (e) {
                // Cross-origin or other security restrictions
                console.debug("Could not access iframe content:", e);
            }
        };

        iframe.addEventListener('load', handleLoad);
        return () => {
            iframe.removeEventListener('load', handleLoad);
        };
    }, []);

    return (
        <iframe
            ref={iframeRef}
            srcDoc={htmlContent}
            className="w-full h-full border-0"
            title={title || "UI Resource"}
            sandbox="allow-scripts allow-same-origin allow-popups"
            allow="geolocation"
        />
    );
};

const CONTAINER_DETAIL_BASE = "/shipper/containers";

/** True if value looks like a container UUID (used for detail link). */
function isContainerUuid(value: unknown): value is string {
    if (typeof value !== "string") return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(value);
}

export const UIResourceRenderer = ({ resource, className }: UIResourceRendererProps) => {
    const frameSize = resource._meta?.["mcpui.dev/ui-preferred-frame-size"] || {
        width: 800,
        height: 600
    };

    const initialData = resource._meta?.["mcpui.dev/ui-initial-render-data"];
    const kind = initialData?.kind ?? (resource.uri?.includes("container_created") ? "container_created" : resource.uri?.includes("docs_recap") ? "docs_recap_table" : resource.uri?.includes("risk_carousel") ? "risk_carousel" : resource.uri?.includes("containers_table") ? "containers_table" : null);

    // Eve Care: container_created – card + link to detail
    if (kind === "container_created" && initialData?.containerId) {
        return (
            <div className={`rounded-xl border border-secondary bg-primary p-4 shadow-xs ${className || ""}`}>
                <h4 className="text-lg font-semibold text-primary mb-2">Container creato</h4>
                <p className="text-sm text-primary mb-2">
                    <span className="font-medium">{initialData.containerNumber || initialData.containerId}</span>
                    {initialData.message && <span className="ml-2 text-tertiary">{initialData.message}</span>}
                </p>
                <Link to={`${CONTAINER_DETAIL_BASE}/${initialData.containerId}`} target="_blank" rel="noopener noreferrer">
                    <Button color="primary" size="sm">Vai al dettaglio</Button>
                </Link>
            </div>
        );
    }

    // Eve Care: docs_recap_table / containers_table – table with container link (no extra UUID column)
    if ((kind === "docs_recap_table" || kind === "containers_table") && Array.isArray(initialData?.rows) && initialData.rows.length > 0) {
        const rows = initialData.rows as Record<string, unknown>[];
        const allColumns = Object.keys(rows[0] ?? {});
        // Hide container_id column: use it only for the link URL, show container_number as "Container ID" with link
        const displayColumns = allColumns.filter((c) => c.toLowerCase() !== "container_id");
        const containerDisplayKey = displayColumns.find((c) => c.toLowerCase().includes("container_number")) ?? displayColumns[0];
        return (
            <div className={`overflow-x-auto rounded-lg border border-secondary ${className || ""}`}>
                <table className="min-w-full border-collapse border border-secondary text-sm">
                    <thead>
                        <tr className="border-b border-secondary bg-secondary/50">
                            {displayColumns.map((col) => (
                                <th key={col} className="border border-secondary px-4 py-2 text-left font-semibold text-primary">
                                    {col.replace(/_/g, " ")}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, idx) => (
                            <tr key={idx} className="border-b border-secondary">
                                {displayColumns.map((col) => {
                                    const val = row[col];
                                    const str = val != null ? String(val) : "";
                                    const isContainerNumberCol = col === containerDisplayKey;
                                    const linkUuid = isContainerUuid(row.container_id) ? (row.container_id as string) : undefined;
                                    const displayText = isContainerNumberCol ? (String(row.container_number ?? row[col] ?? "")) : str;
                                    return (
                                        <td key={col} className="border border-secondary px-4 py-2 text-primary">
                                            {isContainerNumberCol && linkUuid ? (
                                                <Link to={`${CONTAINER_DETAIL_BASE}/${linkUuid}`} className="text-brand hover:underline" target="_blank" rel="noopener noreferrer">
                                                    {displayText}
                                                </Link>
                                            ) : (
                                                str
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    // Eve Care: risk_carousel – horizontal scroll of cards linking to container detail
    if (kind === "risk_carousel" && Array.isArray(initialData?.items) && initialData.items.length > 0) {
        const items = initialData.items as Array<{ container_id?: string; container_number?: string; risk_level?: string; overall_risk_score?: number }>;
        return (
            <div className={`flex gap-3 overflow-x-auto pb-2 ${className || ""}`}>
                {items.map((item, idx) => {
                    const linkUuid = isContainerUuid(item.container_id) ? item.container_id : undefined;
                    const key = linkUuid ?? item.container_number ?? String(idx);
                    const content = (
                        <>
                            <div className="font-semibold text-primary">{item.container_number ?? key}</div>
                            {item.risk_level && <div className="text-sm text-tertiary">{item.risk_level}</div>}
                            {item.overall_risk_score != null && <div className="text-xs text-tertiary">Score: {Number(item.overall_risk_score).toFixed(2)}</div>}
                        </>
                    );
                    return linkUuid ? (
                        <Link
                            key={key}
                            to={`${CONTAINER_DETAIL_BASE}/${linkUuid}`}
                            className="flex-shrink-0 w-48 rounded-xl border border-secondary bg-primary p-3 shadow-xs hover:ring-2 hover:ring-brand transition-all"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {content}
                        </Link>
                    ) : (
                        <div key={key} className="flex-shrink-0 w-48 rounded-xl border border-secondary bg-primary p-3 shadow-xs">
                            {content}
                        </div>
                    );
                })}
            </div>
        );
    }

    // Check if this is a container route map with data in metadata
    const containerData = useMemo(() => {
        const initialData = resource._meta?.["mcpui.dev/ui-initial-render-data"];

        // Also check if resource name suggests it's a route map
        const isRouteMap = resource.name?.toLowerCase().includes("route map") ||
            resource.name?.toLowerCase().includes("container") ||
            resource.uri?.toLowerCase().includes("route") ||
            resource.uri?.toLowerCase().includes("map");

        if (!initialData && !isRouteMap) return null;

        // Check if this looks like container route map data
        if (initialData && (initialData.container || initialData.waypoints || initialData.containerId)) {
            // Try to construct a Container object from the data
            const container: Partial<Container> = {
                id: initialData.containerId || initialData.container?.id || "",
                containerNumber: initialData.containerNumber || initialData.container?.containerNumber || "",
                route: initialData.route || initialData.container?.route || { from: "", to: "" },
                progress: initialData.progress ?? initialData.container?.progress ?? 0,
                waypoints: initialData.waypoints || initialData.container?.waypoints || undefined,
                status: initialData.status || initialData.container?.status || "In Transit",
                createdAt: initialData.createdAt
                    ? new Date(initialData.createdAt)
                    : initialData.container?.createdAt
                        ? new Date(initialData.container.createdAt)
                        : new Date(),
            };

            // If we have waypoints directly, use them
            if (initialData.waypoints && Array.isArray(initialData.waypoints)) {
                container.waypoints = initialData.waypoints as Waypoint[];
            }

            // Validate that we have at least route information
            if (container.route?.from && container.route?.to) {
                return container as Container;
            }
        }

        // Try to extract container data from HTML if it's a route map
        if (isRouteMap && resource.text) {
            try {
                // Try to parse container data from HTML data attributes or script tags
                const parser = new DOMParser();
                const doc = parser.parseFromString(resource.text, 'text/html');

                // Look for data attributes or script tags with container data
                const dataScript = doc.querySelector('script[type="application/json"]');
                if (dataScript && dataScript.textContent) {
                    try {
                        const data = JSON.parse(dataScript.textContent);
                        if (data.container || data.waypoints || data.containerId) {
                            const container: Partial<Container> = {
                                id: data.containerId || data.container?.id || "",
                                containerNumber: data.containerNumber || data.container?.containerNumber || "",
                                route: data.route || data.container?.route || { from: "", to: "" },
                                progress: data.progress ?? data.container?.progress ?? 0,
                                waypoints: data.waypoints || data.container?.waypoints || undefined,
                                status: data.status || data.container?.status || "In Transit",
                                createdAt: data.createdAt
                                    ? new Date(data.createdAt)
                                    : data.container?.createdAt
                                        ? new Date(data.container.createdAt)
                                        : new Date(),
                            };

                            if (container.route?.from && container.route?.to) {
                                return container as Container;
                            }
                        }
                    } catch (e) {
                        // Failed to parse JSON, continue
                    }
                }
            } catch (e) {
                // Failed to parse HTML, continue
            }
        }

        return null;
    }, [resource._meta, resource.name, resource.uri, resource.text]);

    // For container route maps, use React RouteMap component instead of iframe
    if (containerData && resource.mimeType === "text/html") {
        return (
            <div className={`ui-resource-container ${className || ""}`}>
                <div
                    className="rounded-lg border border-secondary overflow-hidden bg-primary"
                    style={{
                        width: "100%",
                        maxWidth: `${frameSize.width}px`,
                        height: `${frameSize.height}px`,
                        minHeight: "400px"
                    }}
                >
                    <RouteMap
                        container={containerData}
                        className="w-full h-full"
                    />
                </div>
            </div>
        );
    }

    // For HTML resources (maps, etc.), render directly in iframe to allow scripts
    // MCP-UI client doesn't execute inline scripts for security, so we need to use iframe directly
    if (resource.mimeType === "text/html" && resource.text) {
        return (
            <div className={`ui-resource-container ${className || ""}`}>
                <div
                    className="rounded-lg border border-secondary overflow-hidden bg-primary"
                    style={{
                        width: "100%",
                        maxWidth: `${frameSize.width}px`,
                        height: `${frameSize.height}px`,
                        minHeight: "400px"
                    }}
                >
                    <MapIframe htmlContent={resource.text} title={resource.name} />
                </div>
            </div>
        );
    }

    // For other resource types, use MCP-UI client
    const mcpResource = useMemo(() => {
        // MCP-UI expects a specific format, ensure compatibility
        return {
            uri: resource.uri,
            name: resource.name || "UI Resource",
            mimeType: resource.mimeType,
            text: resource.text,
            blob: resource.blob,
            _meta: resource._meta || {}
        };
    }, [resource]);

    const handleUIAction = async (action: any): Promise<unknown> => {
        // Handle UI actions from the resource if needed
        console.log("UI action received:", action);
        return Promise.resolve();
    };

    return (
        <div className={`ui-resource-container ${className || ""}`}>
            <div
                className="rounded-lg border border-secondary overflow-hidden bg-primary"
                style={{
                    width: "100%",
                    maxWidth: `${frameSize.width}px`,
                    height: `${frameSize.height}px`,
                    minHeight: "400px"
                }}
            >
                <MCPUIResourceRenderer
                    resource={mcpResource}
                    onUIAction={handleUIAction}
                />
            </div>
        </div>
    );
};

